import type { ReactNode } from 'react'
import { useEffect } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'

import { IconClose } from '@/components/icons'

/**
 * Φύλλο που ανεβαίνει από κάτω στο κινητό και κάθεται στο κέντρο σε οθόνη.
 *
 * Για γρήγορες καταχωρίσεις μέσα από άλλη σελίδα: ο χρήστης προσθέτει προϊόν
 * από τη λίστα του χωρίς να χάσει τη θέση του ούτε τις ποσότητες που έγραψε.
 */
export function Sheet({
  title,
  onClose,
  children,
}: {
  title: string
  onClose: () => void
  children: ReactNode
}) {
  const { t } = useTranslation()

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    // Η σελίδα από πίσω δεν κυλά όσο είναι ανοιχτό.
    const overflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = overflow
    }
  }, [onClose])

  // Στο body: το περιεχόμενο της σελίδας έχει animation με transform, που θα
  // έκανε το `fixed` σχετικό με αυτό και θα έκρυβε το φύλλο κάτω από την μπάρα.
  return createPortal(
    <div className="fixed inset-0 z-40 flex items-end justify-center md:items-center md:p-6">
      <button
        aria-label={title}
        tabIndex={-1}
        onClick={onClose}
        className="absolute inset-0 cursor-default bg-gray-900/40"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="relative flex max-h-[92vh] w-full max-w-lg flex-col rounded-t-3xl bg-white shadow-lift md:rounded-3xl"
      >
        <div className="flex items-center justify-between gap-3 border-b border-gray-100 px-5 py-4">
          <h2 className="truncate text-lg font-bold text-gray-900">{title}</h2>
          <button
            onClick={onClose}
            aria-label={t('common.close')}
            className="flex size-10 shrink-0 items-center justify-center rounded-xl text-gray-500 hover:bg-gray-100"
          >
            <IconClose size={20} />
          </button>
        </div>
        <div className="overflow-y-auto px-5 py-4 pb-[calc(1rem+env(safe-area-inset-bottom))]">
          {children}
        </div>
      </div>
    </div>,
    document.body,
  )
}
